import { Form, Head, router } from '@inertiajs/react'
import { Trash2 } from 'lucide-react'

import Heading from '@/components/heading'
import InputError from '@/components/input-error'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Spinner } from '@/components/ui/spinner'
import AppLayout from '@/layouts/app-layout'
import { adminUserPath, adminUsersPath, editAdminUserPath } from '@/routes'
import type { BreadcrumbItem, User } from '@/types'

interface AdminUserEditProps {
  user: Pick<User, 'id' | 'name' | 'email' | 'role'>
}

export default function AdminUserEdit({ user }: AdminUserEditProps) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: 'Users', href: adminUsersPath() },
    { title: user.name, href: editAdminUserPath(user.id) },
    { title: 'Edit', href: editAdminUserPath(user.id) },
  ]

  function handleDelete() {
    if (
      confirm(
        `Are you sure you want to delete "${user.name}"? This action cannot be undone and will log out all their sessions.`,
      )
    ) {
      router.delete(adminUserPath(user.id))
    }
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`Edit ${user.name}`} />

      <div className="p-6">
        <Heading title={`Edit ${user.name}`} description="Update user information and role" />

        <Card className="mt-6 max-w-2xl">
          <CardHeader>
            <CardTitle>User Details</CardTitle>
            <CardDescription>
              Update the user&apos;s information. Leave password fields empty to keep current password.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Form method="put" action={adminUserPath(user.id)} className="space-y-6">
              {({ processing, errors }) => (
                <>
                  <div className="grid gap-2">
                    <Label htmlFor="name">Name *</Label>
                    <Input
                      id="name"
                      name="user[name]"
                      type="text"
                      required
                      defaultValue={user.name}
                    />
                    <InputError messages={errors['user.name']} />
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      name="user[email]"
                      type="email"
                      required
                      defaultValue={user.email}
                    />
                    <InputError messages={errors['user.email']} />
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="password">New Password (optional)</Label>
                    <Input
                      id="password"
                      name="user[password]"
                      type="password"
                      placeholder="Leave empty to keep current password"
                    />
                    <InputError messages={errors['user.password']} />
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="password_confirmation">Confirm New Password</Label>
                    <Input
                      id="password_confirmation"
                      name="user[password_confirmation]"
                      type="password"
                      placeholder="Re-enter new password if changing"
                    />
                    <InputError messages={errors['user.password_confirmation']} />
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="role">Role *</Label>
                    <select
                      id="role"
                      name="user[role]"
                      required
                      defaultValue={user.role || 'admin'}
                      className="border-input bg-background flex h-10 w-full rounded-md border px-3 py-2 text-sm"
                    >
                      <option value="admin">Admin</option>
                      <option value="superadmin">Superadmin</option>
                    </select>
                    <InputError messages={errors['user.role']} />
                  </div>

                  {errors.base && (
                    <div className="rounded-md bg-destructive/10 p-3">
                      <p className="text-destructive text-sm">{errors.base}</p>
                    </div>
                  )}

                  <div className="flex justify-between">
                    <Button type="button" variant="destructive" onClick={handleDelete}>
                      <Trash2 className="mr-2 h-4 w-4" />
                      Delete User
                    </Button>
                    <div className="flex gap-4">
                      <Button type="button" variant="outline" onClick={() => history.back()}>
                        Cancel
                      </Button>
                      <Button type="submit" disabled={processing}>
                        {processing && <Spinner />}
                        Save Changes
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </Form>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  )
}
